import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { DndContext, DragOverlay, PointerSensor, TouchSensor, useDraggable, useDroppable, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core';
import type { Lead, Pipeline } from '@shared/types';
import { stageStyle } from '@shared/stageColors';
import { useSetStage, useStages, useToast } from '../store';
import { sortByDateDesc, type GroupedLeads } from '../filter';
import { Empty, Icons } from './ui';
import { StagePill } from './StagePill';

const OTHER = '__other__';

/**
 * One column per status group, across both pipelines — the same grouping as the table and the mobile tabs. A card shows
 * its exact status in the top corner (click to change it) and a status-coloured edge. Dropping a card into another
 * column moves the lead to that group's first status that exists in the lead's pipeline.
 */
export function Kanban({ groups, pipelines, onOpen }: { groups: GroupedLeads[]; pipelines: Pipeline[]; onOpen: (id: string) => void }) {
  const setStage = useSetStage();
  const toast = useToast();
  const { stageName } = useStages();
  const [active, setActive] = useState<Lead | null>(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 6 } }), useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 8 } }));

  const onDragEnd = (e: DragEndEvent) => {
    setActive(null);
    const lead = e.active.data.current?.lead as Lead | undefined;
    const target = groups.find((g) => g.group.id === e.over?.id)?.group;
    if (!lead || !target || target.id === OTHER) return;
    if (target.stageNames.includes(stageName(lead.stageId))) return; // already in this group
    const pipeline = pipelines.find((p) => p.id === lead.pipelineId);
    const stage = target.stageNames.map((n) => pipeline?.stages.find((s) => s.name === n)).find((s) => !!s);
    if (!stage) { toast(`לקבוצה "${target.name}" אין סטטוס בפייפליין של הליד`, 'error'); return; }
    setStage.mutate({ id: lead.id, stageId: stage.id });
  };

  return (
    <DndContext sensors={sensors} onDragStart={(e) => setActive(e.active.data.current?.lead as Lead)} onDragEnd={onDragEnd} onDragCancel={() => setActive(null)}>
      <ScrollBoard>
        {groups.map((g) => <Column key={g.group.id} data={g} onOpen={onOpen} />)}
      </ScrollBoard>
      <DragOverlay>{active ? <Card lead={active} dragging /> : null}</DragOverlay>
    </DndContext>
  );
}

/**
 * The horizontal board. Its scrollbar sits on top (a mirrored track kept in sync), where it's seen, and the empty space
 * — between columns, under the cards, the headers — can be grabbed and dragged sideways. Cards keep their own drag.
 */
function ScrollBoard({ children }: { children: React.ReactNode }) {
  const board = useRef<HTMLDivElement>(null);
  const top = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [grabbing, setGrabbing] = useState(false);
  const drag = useRef<{ x: number; left: number } | null>(null);

  useEffect(() => {
    const el = board.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.scrollWidth));
    ro.observe(el);
    for (const c of Array.from(el.children)) ro.observe(c);
    setWidth(el.scrollWidth);
    return () => ro.disconnect();
  }, [children]);

  // keep the two scroll positions together (both are RTL, so scrollLeft means the same thing in each)
  const sync = (from: HTMLDivElement | null, to: HTMLDivElement | null) => { if (from && to && Math.abs(to.scrollLeft - from.scrollLeft) > 0.5) to.scrollLeft = from.scrollLeft; };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0 || (e.target as HTMLElement).closest('[data-kanban-card],button,a,input,select,textarea')) return;
    drag.current = { x: e.clientX, left: e.currentTarget.scrollLeft };
    e.currentTarget.setPointerCapture(e.pointerId);
    setGrabbing(true);
  };
  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    e.currentTarget.scrollLeft = drag.current.left - (e.clientX - drag.current.x); // same formula in LTR and RTL
  };
  const end = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!drag.current) return;
    drag.current = null;
    setGrabbing(false);
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const overflows = width > (board.current?.clientWidth ?? 0) + 1;
  return (
    <div>
      <div ref={top} className={`kanban-scrollbar mb-2 overflow-x-auto overflow-y-hidden ${overflows ? '' : 'invisible'}`} onScroll={() => sync(top.current, board.current)} aria-hidden="true">
        <div style={{ width, height: 1 }} />
      </div>
      <div ref={board} onScroll={() => sync(board.current, top.current)}
        className={`flex gap-3 overflow-x-auto pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${grabbing ? 'cursor-grabbing select-none' : 'cursor-grab'}`}
        style={{ minHeight: '60vh' }}
        onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={end} onPointerCancel={end}>
        {children}
      </div>
    </div>
  );
}

function Column({ data, onOpen }: { data: GroupedLeads; onOpen: (id: string) => void }) {
  const { setNodeRef, isOver } = useDroppable({ id: data.group.id, disabled: data.group.id === OTHER });
  const rows = sortByDateDesc(data.leads);
  return (
    <div ref={setNodeRef} className={`flex w-72 shrink-0 flex-col rounded-xl border bg-page/60 ${isOver ? 'border-accent ring-2 ring-accent-soft' : 'border-line'}`}>
      <header className="flex items-center gap-2 rounded-t-xl border-b border-line bg-panel px-3 py-2 text-sm font-semibold">
        <span className="truncate">{data.group.name}</span>
        <span className="ms-auto rounded-full bg-page px-2 text-xs font-medium tabular-nums text-ink-2">{data.leads.length.toLocaleString('he-IL')}</span>
      </header>
      <div className="flex max-h-[70vh] flex-col gap-2 overflow-y-auto p-2">
        {rows.slice(0, 200).map((l) => <DraggableCard key={l.id} lead={l} onOpen={() => onOpen(l.id)} />)}
        {rows.length > 200 && <p className="text-center text-xs text-ink-3">ועוד {rows.length - 200}…</p>}
        {rows.length === 0 && <p className="p-4 text-center text-xs text-ink-3">אין לידים</p>}
      </div>
    </div>
  );
}

function DraggableCard({ lead, onOpen }: { lead: Lead; onOpen: () => void }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: lead.id, data: { lead } });
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} data-kanban-card style={{ opacity: isDragging ? 0.4 : 1, touchAction: 'none' }} onClick={onOpen}>
      <Card lead={lead} onOpen={onOpen} />
    </div>
  );
}

/** Name with the status in the top (left) corner — the status gives way first when space is tight — then source and notes. */
function Card({ lead, dragging, onOpen }: { lead: Lead; dragging?: boolean; onOpen?: () => void }) {
  const { stageName } = useStages();
  const st = stageStyle(stageName(lead.stageId));
  return (
    <div className={`card relative cursor-grab overflow-hidden p-2.5 ps-3.5 text-sm ${dragging ? 'shadow-xl' : ''} ${st.dimmed ? 'opacity-70' : ''}`}>
      <div className="absolute inset-y-0 start-0 w-[3px]" style={{ background: st.stripe }} aria-hidden="true" />
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 font-semibold [flex:0_1_auto]"><span dir="auto" className="min-w-0 truncate">{lead.fullName || <Empty />}</span></div>
        <div className="ms-auto flex min-w-11 justify-end [flex:0_100_auto]"><StagePill lead={lead} variant="card" onChanged={onOpen} /></div>
      </div>
      <div className="mt-1.5 flex items-center gap-2 text-xs text-ink-2">
        <span className="flex min-w-0 flex-1"><span dir="auto" className="min-w-0 truncate">{lead.source ?? '—'}</span></span>
        <span className="inline-flex shrink-0 items-center gap-1 text-ink-3"><Icons.Note size={12} />{lead.noteCount}</span>
      </div>
    </div>
  );
}
